import { BasicModel } from 'src/packages/mitsBasics';
import { ITemplateRow } from 'src/packages/templateManagement/models';
import { ITemplateBasicElement } from 'src/packages/templateManagement/models/elements/tp-basic-element';
import { ITemplateBooleanElement } from 'src/packages/templateManagement/models/elements/tp-boolean-element';
import { ITemplateGalleryElement } from 'src/packages/templateManagement/models/elements/tp-gallery-element';
import { ITemplateStringElement } from 'src/packages/templateManagement/models/elements/tp-string-element';
import { ITemplateTableElement } from 'src/packages/templateManagement/models/elements/tp-table-element';
import { ITemplateTextElement } from 'src/packages/templateManagement/models/elements/tp-text-element';
import { ITemplateTextareaElement } from 'src/packages/templateManagement/models/elements/tp-textarea-element';
import { ITemplateTextStyle } from 'src/packages/templateManagement/models/tp-text-style';
import { IMitsPDF } from '../../../../interfaces';
import { MitsPDFAssets } from '../mits-pdf-assets';
import { MitsPDFFormatter } from '../mits-pdf-formatter';
import { ImageGenerator } from './imageGenerator';

/**
 * Generiert Module für den PDF-Bericht
 */
export class ModuleGenerator {
  private styleCounter = 0;
  private parentKeys: string[] = [];

  constructor(
    public object: BasicModel,
    private docDef: IMitsPDF,
    private imageGenerator: ImageGenerator,
    private mitsFormatter: MitsPDFFormatter,
    private mitsAssets: MitsPDFAssets
  ) {}

  /**
   * Generiert auf Basis eines Types ein Element was auf dem PDF-Bericht gerendert werden kann
   * @param templateElement to parse for the pdf document
   * @returns parsed template for pdf document
   */
  public parseTemplateModule(templateElement: ITemplateBasicElement): unknown {
    // Entfernt eventuelle ParentKeys aus der Selektion
    this.clearParentAttributeKeys(templateElement);

    switch (templateElement.type) {
      case 'TemplateManagement::Modules::Text':
        return this.parseTemplateTextModule(templateElement);
      case 'TemplateManagement::Modules::String':
        return this.parseTemplateStringModule(
          templateElement as ITemplateStringElement
        );
      case 'TemplateManagement::Modules::Boolean':
        return this.parseTemplateBooleanModule(
          templateElement as ITemplateBooleanElement
        );
      case 'TemplateManagement::Modules::TextArea':
        return this.parseTemplateTextAreaModule(
          templateElement as ITemplateTextareaElement
        );
      case 'TemplateManagement::Modules::Gallery':
        return this.parseTemplateGalleryModule(
          templateElement as ITemplateGalleryElement
        );
      case 'TemplateManagement::Modules::Table':
        return this.parseTemplateTableModule(
          templateElement as ITemplateTableElement
        );
      default:
        return {
          text: '[ERROR] No Module-Parser for PDF found!',
        };
    }
  }

  /**
   * Wandelt ein Text-Element um
   * @param templateElement of type text
   * @returns parsed template for pdf document
   */
  private parseTemplateTextModule(templateElement: ITemplateTextElement): any {
    return {
      text: templateElement.title,
      style: this.attachTextStyle(templateElement.title_text_style),
    };
  }

  /**
   * Wandelt ein Ja/Nein-Feld um
   * @param templateElement of type boolean
   * @returns parsed template for pdf document
   */
  private parseTemplateBooleanModule(
    templateElement: ITemplateBooleanElement
  ): any {
    const boolVal: boolean = this.getAttributeValue(
      this.object,
      templateElement.attribute_key
    );
    const textSize = templateElement.title_text_style.size.toString();
    let boolValObject = {};
    if (templateElement.style_type === 'yes-no-text') {
      boolValObject = {
        text: boolVal ? 'Ja' : 'Nein',
        style: this.attachTextStyle(templateElement.title_text_style),
      };
    } else if (templateElement.style_type === 'checkbox') {
      boolValObject = {
        image: this.imageGenerator.attachImage(
          boolVal ? this.mitsAssets.icons.check : this.mitsAssets.icons.close
        ),
        fit: [textSize, textSize],
      };
    }
    return {
      table: {
        body: [
          [
            {
              text: templateElement.title,
              style: this.attachTextStyle(templateElement.title_text_style),
            },
            boolValObject,
          ],
        ],
      },
      layout: {
        defaultBorder: false,
        paddingLeft(i: number) {
          return 0;
        },
      },
    };
  }

  /**
   * Wandelt eine Textarea um
   * @param templateElement of type textarea
   * @returns parsed template for pdf document
   */
  private parseTemplateTextAreaModule(
    templateElement: ITemplateTextareaElement
  ): any {
    const elements = [];

    if (!templateElement.hideTitle) {
      elements.push({
        text: templateElement.title,
        style: this.attachTextStyle(templateElement.title_text_style),
      });
    }

    elements.push({
      text: this.getAttributeValue(this.object, templateElement.attribute_key),
      style: this.attachTextStyle(templateElement.body_text_style),
    });

    return {
      stack: elements,
    };
  }

  /**
   * Tabelle rendern
   * @param templateElement of type table
   * @return parsed template for pdf document
   */
  private parseTemplateTableModule(templateElement: ITemplateTableElement): unknown {
    const positions = (this.object as Record<string, unknown>)[
      templateElement.attribute_key ?? ''
    ];

    const returnValue = {
      style: this.attachTextStyle(templateElement.title_text_style),
      layout: templateElement.style_type,
      table: {
        headerRows: 1,
        body: this.renderTableRows(positions, templateElement),
      },
    };

    return returnValue;
  }

  /**
   * Zeilen einer Tabelle rendern
   * @param positions to render
   * @param templateElement the template Element
   */
  private renderTableRows(
    positions: unknown,
    templateElement: ITemplateTableElement
  ): unknown[] {
    // Objekt speichern, damit is später wieder gesetzt werden kann
    const savedObject = this.object;

    // Header rendern
    // Hinweis: templateElement.rows NICHT mutieren (kein shift()), da das
    // Template-Modul-Objekt bei template_for (z.B. mehrere Maschinen) pro
    // Durchlauf wiederverwendet wird. Sonst fehlt ab der 2. Maschine die Tabelle.
    const [header, ...bodyRows] = templateElement.rows;
    let rowsToReturn: unknown[] = [
      header.cols.map((col) => {
        return this.parseTemplateModule(col.module);
      }),
    ];

    this.addParentKey(templateElement.attribute_key ?? '');

    // Einzelne Positionen rendern
    // TODO Der Filter kann auch dynamisch gelöst werden
    const positionList: unknown[] = Array.isArray(positions) ? positions : [];
    positionList
      .filter(
        (rowObject) =>
          !(rowObject as { report_canceled?: boolean }).report_canceled
      )
      .forEach((position) => {
        this.object = position as BasicModel;
        const rows = bodyRows.map((row) => {
          return row.cols.map((col) => {
            // Titel Funktion deaktivieren, wenn es innerhalb einer Tabelle angezeigt wird
            col.module.hideTitle = true;
            const generatedModule = this.parseTemplateModule(col.module);
            return generatedModule;
          });
        });
        rowsToReturn = rowsToReturn.concat(rows);
      });

    this.popParentKey();

    // Objekt zurücksetzen, damit weiterer Rendervorgang normal weiterläuft
    this.object = savedObject;

    return rowsToReturn;
  }

  public addParentKey(key: string) {
    if (this.parentKeys.includes(key) === false) this.parentKeys.push(key);
  }

  public clearParentKeys() {
    this.parentKeys = [];
  }

  public popParentKey(): string | undefined {
    return this.parentKeys.pop();
  }

  private clearParentAttributeKeys(module: ITemplateBasicElement) {
    this.parentKeys.forEach((parentKey) => {
      this.clearParentAttributeKey(module, parentKey);
    });
  }

  public clearParentKeysTemplateFor(module: ITemplateRow) {
    this.parentKeys.forEach((parentKey) => {
      if (module.template_for) {
        module.template_for = module.template_for.replace(parentKey + '.', '');
      }
    });
  }

  /**
   * Löscht den ParentKey aus dem Modul
   * @param module to clear key of
   * @param parentKey key to clear
   */
  private clearParentAttributeKey(
    module: ITemplateBasicElement,
    parentKey: string
  ) {
    if (module.attribute_key) {
      module.attribute_key = module.attribute_key.replace(parentKey + '.', '');
    }
  }

  /**
   * String transformieren
   * @param templateElement of type string
   * @returns parsed template for pdf document
   */
  private parseTemplateStringModule(
    templateElement: ITemplateStringElement
  ): any {
    const elements = [];

    if (!templateElement.hideTitle) {
      elements.push({
        text: templateElement.title,
        style: this.attachTextStyle(templateElement.title_text_style),
      });
    }

    elements.push({
      text: this.getAttributeValue(this.object, templateElement.attribute_key),
      style: this.attachTextStyle(templateElement.body_text_style),
    });

    return {
      text: elements,
    };
  }

  /**
   * Text-Stil zu einem Element hinzufügen
   * @param templateTextStyle to attach to the pdf-document
   * @returns the name of the variable in wich this style got saved
   */
  private attachTextStyle(templateTextStyle: ITemplateTextStyle): any {
    const name = 'textStyle' + this.styleCounter;
    this.docDef.styles[name] = {
      fontSize: templateTextStyle.size,
      bold: templateTextStyle.weight >= 600,
      italics: templateTextStyle.italic,
      alignment: templateTextStyle.align,
      decoration: templateTextStyle.underline ? 'underline' : null,
    };
    this.styleCounter++;
    return name;
  }

  /**
   * Bildergalerie rendern
   * Die Bilder liegen am aktuellen Objekt bereits als DataURLs vor
   * (z.B. Schaden.images_data_urls beim Schadensbericht), daher ist kein
   * asynchrones Nachladen nötig.
   * @param templateElement of type gallery
   * @returns parsed template for pdf document
   */
  private parseTemplateGalleryModule(
    templateElement: ITemplateGalleryElement
  ): any {
    let dataUrls = this.getGalleryImageDataUrls(templateElement);

    // thumbnail gesetzt -> nur dieses eine Bild anzeigen, sonst alle
    if (templateElement.thumbnail !== null && templateElement.thumbnail !== undefined) {
      const single = dataUrls[templateElement.thumbnail];
      dataUrls = single ? [single] : [];
    }

    if (dataUrls.length === 0) {
      return { text: '' };
    }

    const size = Number(templateElement.width) || 120;
    // Bilder nebeneinander rendern (columns). Die linke Einrückung kommt vom
    // umschliessenden Spalten-Wrapper und entspricht damit der Tabelle.
    // width: 'auto' -> jede Spalte nimmt nur die Bildbreite ein.
    return {
      columns: dataUrls.map((dataUrl) => ({
        width: 'auto',
        image: this.imageGenerator.attachImage(dataUrl),
        fit: [size, size],
      })),
      columnGap: 8,
    };
  }

  /**
   * Lädt die Bild-DataURLs für die Galerie aus dem aktuellen Objekt.
   * Primär über den konfigurierten attribute_key (ParentKeys wurden bereits
   * entfernt), mit Fallback auf das Schadens-Feld `images_data_urls`.
   * @param templateElement of type gallery
   * @returns array of image dataURLs
   */
  private getGalleryImageDataUrls(
    templateElement: ITemplateGalleryElement
  ): string[] {
    let candidate: any = templateElement.attribute_key
      ? this.getRawAttributeValue(this.object, templateElement.attribute_key)
      : undefined;

    if (!Array.isArray(candidate)) {
      candidate = (this.object as Record<string, unknown>)['images_data_urls'];
    }

    if (!Array.isArray(candidate)) {
      return [];
    }

    return candidate.filter(
      (value): value is string => typeof value === 'string' && value.length > 0
    );
  }

  /**
   * Liest einen (ggf. verschachtelten) Attributwert OHNE Formatierung aus.
   * Im Gegensatz zu getAttributeValue bleibt der Rohwert (z.B. ein Array)
   * erhalten.
   * @param obj to load attribute from
   * @param attribute dot-separated attribute path
   * @returns raw value of the object-attribute
   */
  private getRawAttributeValue(obj: any, attribute: string): any {
    if (!obj || !attribute) {
      return undefined;
    }
    return attribute
      .split('.')
      .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  }

  /**
   * Attribute aus einem Objekt laden
   * @param obj to load attribute from
   * @param attribute to get value of
   * @returns value of the object-attribute
   */
  private getAttributeValue(obj: any, attribute: string | null): any {
    if (!attribute) return '';
    if (attribute.includes('.')) {
      const attributes = attribute.split('.');
      const nowAttribute = attributes.shift();
      const nextAttribute = attributes.join('.');
      if (nowAttribute && obj && obj[nowAttribute]) {
        return this.getAttributeValue(obj[nowAttribute], nextAttribute);
      } else {
        return '';
      }
    } else {
      return this.mitsFormatter.checkAndFormatValue(obj[attribute]);
    }
  }
}
